import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { WarningIcon as Warning, InfoIcon as Info, HeartIcon as Heart, ArrowsClockwiseIcon as ArrowsClockwise } from "@phosphor-icons/react";

/** Severity → visual treatment. Tuned to match the gold/black/red palette so
 *  the warning blends with the rest of the registry instead of looking like a
 *  generic Bootstrap alert. */
const TONE = {
  none:     { bg: "bg-[#F3FBF4]", border: "border-[#BCE5C2]", text: "text-[#1B5E20]", iconCls: "text-[#2E7D32]", Icon: Heart, label: "No common ancestry" },
  trace:    { bg: "bg-[#F3FBF4]", border: "border-[#BCE5C2]", text: "text-[#1B5E20]", iconCls: "text-[#2E7D32]", Icon: Heart, label: "Trace ancestry" },
  mild:     { bg: "bg-[#FBF7EC]", border: "border-[#E8D9A6]", text: "text-[#7C5E12]", iconCls: "text-[#C9A227]", Icon: Info,  label: "Mild inbreeding" },
  moderate: { bg: "bg-[#FFF7E6]", border: "border-[#F5C572]", text: "text-[#7C4A06]", iconCls: "text-[#D17E00]", Icon: Warning, label: "Moderate inbreeding" },
  high:     { bg: "bg-[#FEF2F2]", border: "border-[#FCA5A5]", text: "text-[#991B1B]", iconCls: "text-[#C1272D]", Icon: Warning, label: "High inbreeding" },
  severe:   { bg: "bg-[#FEF2F2]", border: "border-[#C1272D]", text: "text-[#7F1D1D]", iconCls: "text-[#C1272D]", Icon: Warning, label: "Severe inbreeding" },
};

/** Registration-time inbreeding-coefficient preview.
 *
 *  Auto-computes whenever both `sireId` and `damId` are set, debounced 350ms
 *  to avoid hammering the API while the user is still picking. Hidden when
 *  either side is missing (the form's `Pedigree` section already has its own
 *  empty-state UX). Uses the existing `/api/dogs/inbreeding-preview`
 *  endpoint which returns Wright's COI percent + a severity bucket + a
 *  human-readable warning message.
 */
export default function InbreedingPreview({ sireId, damId, generations = 5 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!sireId || !damId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const { data: payload } = await api.get("/dogs/inbreeding-preview", {
          params: { sire_id: sireId, dam_id: damId, generations },
        });
        setData(payload);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timerRef.current);
  }, [sireId, damId, generations]);

  if (!sireId || !damId) return null;

  if (loading && !data) {
    return (
      <div
        className="mt-4 rounded-lg border border-dashed border-[#E5E5E5] bg-[#FAFAFA] px-4 py-3 text-xs text-[#6B6B6B] flex items-center gap-2"
        data-testid="inbreeding-preview-loading"
      >
        <ArrowsClockwise size={12} className="animate-spin" />
        Checking pedigree overlap between sire and dam…
      </div>
    );
  }

  if (!data) return null;

  const tone = TONE[data.severity] || TONE.none;
  const Icon = tone.Icon;
  const pct = (data.percent ?? 0).toFixed(2);
  const ancestors = data.common_ancestors || [];

  return (
    <div
      className={`mt-4 rounded-lg border ${tone.bg} ${tone.border} px-4 py-3`}
      data-testid="inbreeding-preview"
      data-severity={data.severity}
    >
      <div className="flex items-start gap-3">
        <Icon size={18} weight="duotone" className={`${tone.iconCls} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className={`text-sm font-semibold ${tone.text}`} data-testid="inbreeding-preview-label">
              {tone.label} · <span className="font-mono">{pct}%</span>
            </p>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold">
              Wright · {data.max_generations}-gen
            </span>
          </div>
          <p className={`text-xs ${tone.text} opacity-90 mt-1`} data-testid="inbreeding-preview-message">
            {data.warning_message}
          </p>
          {ancestors.length > 0 && (
            <details className="mt-2" data-testid="inbreeding-preview-ancestors">
              <summary className={`text-xs font-semibold cursor-pointer ${tone.text} hover:underline`}>
                {ancestors.length} shared ancestor{ancestors.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-2 space-y-1.5">
                {ancestors.slice(0, 6).map((a) => (
                  <li
                    key={a.id}
                    className="text-xs text-[#3F3F3F] flex items-center justify-between gap-3 bg-white/60 rounded px-2 py-1"
                  >
                    <span className="truncate">
                      <span className="font-semibold">{a.name}</span>
                      {a.registration_number && (
                        <span className="ml-1.5 text-[10px] font-mono text-[#6B6B6B]">{a.registration_number}</span>
                      )}
                    </span>
                    <span className="text-[10px] text-[#6B6B6B] font-mono whitespace-nowrap">
                      sire:{(a.sire_depths || []).join(",")} · dam:{(a.dam_depths || []).join(",")} · +{(a.contribution * 100).toFixed(2)}%
                    </span>
                  </li>
                ))}
                {ancestors.length > 6 && (
                  <li className="text-[10px] text-[#6B6B6B] italic">…and {ancestors.length - 6} more</li>
                )}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
